import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { mergeGuestCartToServer } from "@/lib/guestCart";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=unauth
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setBooting(false); return; }
    // Skip the auth probe on public visits (no prior session) to avoid noisy 401s in the console.
    if (!localStorage.getItem("sfl_had_session")) { setUser(null); setBooting(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => { localStorage.removeItem("sfl_had_session"); setUser(null); })
      .finally(() => setBooting(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    localStorage.setItem("sfl_had_session", "1");
    await mergeGuestCartToServer(api);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", { ...payload, app_origin: window.location.origin });
    if (data.needs_verification) return data;
    setUser(data.user);
    localStorage.setItem("sfl_had_session", "1");
    await mergeGuestCartToServer(api);
    return data.user;
  };

  const claimAccount = async (token, password) => {
    const { data } = await api.post("/auth/claim", { token, password });
    setUser(data.user);
    localStorage.setItem("sfl_had_session", "1");
    await mergeGuestCartToServer(api);
    return data;
  };

  const googleLogin = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    setUser(data.user);
    localStorage.setItem("sfl_had_session", "1");
    await mergeGuestCartToServer(api);
    return data;
  };

  const completeProfile = async (role, company_name) => {
    const { data } = await api.post("/auth/complete-profile", { role, company_name });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("sfl_had_session");
    setUser(null);
  };

  const acceptLegal = async (payload) => {
    await api.post("/auth/accept-legal", payload);
    setUser((u) => ({ ...u, legal_accepted: true }));
  };

  const refreshUser = async () => {
    const r = await api.get("/auth/me");
    setUser(r.data.user);
  };

  const reconsent = async (payload) => {
    await api.post("/auth/reconsent", payload);
    await refreshUser();
  };

  return (
    <AuthContext.Provider value={{ user, booting, login, register, claimAccount, googleLogin, completeProfile, logout, acceptLegal, reconsent, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
